<template>
  <div class="tenant-tech-comment-detail email-preview">
    <div class="comment-detail__header">
      <img
        v-region-img-src="'email-logo-hkland.png'"
        class="hkld-logo"
        alt="Hongkong Land"
      >
      <div
        v-if="showRecipientsReadOnly"
        class="section-mgn-btm recipient-list"
      >
        <div v-if="isTenant">
          <span class="small-mgn-right">{{ $t('to') }}:</span>
          <span>{{ deep(sendRecipients, 'to') }}</span>
        </div>
        <div v-if="isTenant">
          <span class="small-mgn-right">{{ $t('cc') }}:</span>
          <span>{{ deep(sendRecipients, 'cc') }}</span>
        </div>
      </div>
      <div class="greeting section-mgn-btm">
        {{ $t('tenantProjects.tenantViewComments.dearTenantRepresentatives', [tenantCompanyInfo.name]) }}
      </div>
      <div class="tenant-project-location section-mgn-btm">
        <div
          v-if="isGreenFitOut"
          class="font-bold"
        >
          {{ $t('tenantProjects.greenFitOut.emailSubjectPrefix') }}
        </div>
        <div
          v-if="isPostfitout"
        >
          {{ $t('tenantProjects.postFitout.titleWithNumber', [activeBatch.order ? activeBatch.order + 1 : '']) }}
        </div>
        <div
          v-if="shouldNotShowSection"
          class="tenant-project-location"
        >
          {{ isFitout ? $t('tenantProjects.submission.alterationRenoWorks') : $t('tenantProjects.submission.reinstatementWorks') }}
        </div>
        <div
          v-if="!isGreenFitOut"
          class="tenant-project-floor"
        >
          {{ `${(currentTenantProject.projectLocation || []).join(', ')}` }} ("{{ $t('tenancyProject.premises') }}")
        </div>
        <div
          v-if="!isGreenFitOut"
          class="tenant-project-building"
        >
          {{ deep(currentTenantProject, 'tenancy.building.fullName') }} ("{{ $t('tenantProjects.allTenantProjects.building') }}")
        </div>
      </div>
    </div>

    <div class="comment-detail__content section-mgn-btm email-preview__content">
      <div
        v-if="!isTenant && isBatchesView"
        class="design-comment"
      >
        {{ actionTexts[deep(activeBatch, 'designComment.comment')] }}
      </div>
      <div class="consolidated-comment tech-comment">
        <div class="tech-comment-description greeting">
          <div v-html="deep(activeBatch, 'techComment.greetingDescription')" />
        </div>
        <div
          v-if="description"
          class="comment-content description"
          :class="{ 'only-one': onlyDescription }"
        >
          <div
            class="none-x-scroll"
            v-html="description"
          />
        </div>
        <div
          v-for="(section, secIndex) in sortedComments"
          :key="section.id"
          class="section-header"
        >
          <p>{{ getAlphabet(secIndex) }} {{ section.value }}</p>
          <div
            v-for="(line, lineIndex) in section.sectionContent"
            :key="lineIndex"
            class="comment-content"
          >
            <pre class="comment-content__line-value none-x-scroll">{{ getPrefix(lineIndex) }} {{ line.value }}</pre>
          </div>
        </div>
        <div class="tech-comment-description closingDesc section-mgn-btm">
          <div v-html="deep(activeBatch, 'techComment.closingDescription')" />
        </div>
      </div>
    </div>

    <template v-if="shouldNotShowSection">
      <div
        v-if="activeBatchDocs.length > 0"
        class="active-batch__files"
      >
        <label class="files-label">{{ $t('tenantProjects.batchSubmittedFileList',[deep(activeBatch, 'order') + 1]) }}</label>
        <ol
          class="file-list"
          type="1"
        >
          <li
            v-for="(doc) in activeBatchDocs"
            :key="doc.id"
          >
            <span class="file-name">{{ getBatchDocName(doc) }}</span>
          </li>
        </ol>
      </div>
      <div
        v-else
        class="active-batch__files"
      >
        <label class="files-label">{{ $t('tenantProjects.batchSubmittedFileList',[deep(activeBatch, 'order') + 1]) }}: {{ $t('none') }}</label>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { computed, inject, unref } from 'vue-demi'
import { actionTexts } from './consts'
import numberUtils from '@/utils/numberUtils'
import dateUtil from '@/utils/dateUtil'
import * as userUtil from '@/api/users/util'
import userHelper from '@/utils/userHelper'
import { tenantProjectTypes } from '@/utils/locale'
import { getBatchDocName, doDefaultSort } from '@/modules/tenantProjects/utils'
import lodash from 'lodash'
import { sortTechComments } from '@/utils/tenantProjectGuidelineUtils'
import { PREVIEW_TYPES } from '@/modules/eCircular/consts'
import { DEPENDENCY_KEYS, TAB_TYPE } from '../consts'

export default {
  name: 'CommentsPreviewBody',
  props: {
    isBatchesView: {
      type: Boolean,
      default: true,
    },
    activeBatch: {
      type: Object,
      default: () => ({}),
    },
    showRecipientsReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  setup (props) {
    const activedTabType = inject(DEPENDENCY_KEYS.TAB_TYPE)
    const isPostfitout = computed(() => {
      return unref(activedTabType) === TAB_TYPE.POST_FIT_OUT
    })
    const isGreenFitOut = computed(() => {
      return unref(activedTabType) === TAB_TYPE.GREEN_FIT_OUT
    })
    const shouldNotShowSection = computed(() => {
      return !(unref(isPostfitout) || unref(isGreenFitOut))
    })
    const onlyDescription = computed(() => {
      const greetingDescription = lodash.get(props.activeBatch, 'techComment.greetingDescription')
      const closingDescription = lodash.get(props.activeBatch, 'techComment.closingDescription')
      return !greetingDescription && !closingDescription
    })
    return {
      onlyDescription,
      isPostfitout,
      isGreenFitOut,
      shouldNotShowSection,
    }
  },
  data () {
    return {
      activeBatchDocs: [],
      actionTexts,
      dateUtil,
      deep,
      getBatchDocName,
      userHelper,
      userUtil,
    }
  },
  computed: {
    ...mapGetters('tenantProject', ['currentTenantProject']),
    ...mapGetters('user', ['isTenant']),
    tenantCompanyInfo () {
      return deep(this.currentTenantProject, 'tenancy.company') || {}
    },
    description () {
      return deep(this.activeBatch, 'techComment.description')
    },
    sendRecipients () {
      const sendHistory = lodash.get(this.activeBatch, 'actionHistory', []).find(history => history.actionType === PREVIEW_TYPES.SEND)
      return lodash.get(sendHistory, 'emailReceivers', {})
    },
    isFitout () {
      return this.currentTenantProject.type === tenantProjectTypes.Fitout
    },
    sortedComments () {
      const rs = lodash.get(this.activeBatch, 'techComment.comments') || []
      return sortTechComments(rs)
    },
  },
  watch: {
    'currentTenantProject.projectDocs': {
      handler () {
        if (this.activeBatch) {
          this.activeBatchDocs = doDefaultSort(this.currentTenantProject.projectDocs.filter(doc =>
            doc.batchNum !== null && Number(doc.batchNum) === Number(this.activeBatch.order)))
        }
      },
      deep: true,
    },
    activeBatch () {
      if (this.activeBatch) {
        this.activeBatchDocs = doDefaultSort(this.currentTenantProject.projectDocs.filter(doc =>
          doc.batchNum !== null && Number(doc.batchNum) === Number(this.activeBatch.order)))
      }
    },
  },
  methods: {
    getAlphabet (val) {
      return `${numberUtils.convertToAlphabet(parseInt(val + 1))}.`
    },
    getPrefix (index) {
      return `${index + 1}.`
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/email_preview';

.tenant-tech-comment-detail {
  .none-x-scroll {
    // IE bug only - <pre showing horizontal scroll bar
    -ms-overflow-x: hidden;
  }
  .section-mgn-btm {
    margin-bottom: 24px;
  }

  .recipient-list {
    word-wrap: break-word;
  }
  .small-mgn-right {
    margin-right: 5px;
  }

  .consolidated-comment {
    .comment-content {
      padding: 10px 0;
      font-weight: normal;
    }
    .section-header {
      font-weight: bold;
      padding: 12px 0;
    }
    .description.only-one {
      padding: 0;
    }
  }
  .tech-comment-description, .comment-content {
    text-align: justify;
  }
  .active-batch__files {
    .files-label {
      line-height: 20px;
      font-weight: bold;
      font-size: 14px;
      color: #0C3A58;
      margin: 12px 0;
    }
  }
  .tenant-project-location div {
    font-weight: bold;
  }
  .hkld-logo {
    height: 40px;
    margin: 13px 0;
  }

  ::v-deep .tech-comment {
    p {
      margin-bottom: 20px;
    }
    .MsoNormal {
      margin-left: 0;
      font-family: Verdana;
      font-size: 14px;
    }
  }
}
</style>
